import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import { getSkinSrc } from "./SeasonalSkins";
import LoadingSpinner from "./LoadingSpinner";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL || "";
const PAGE_SIZE = 10;
const MAX_CARD_NAME_LEN = 10;

const rarityTabs = [
  { key: "all", label: "All" },
  { key: "common", label: "Common" },
  { key: "rare", label: "Rare" },
  { key: "epic", label: "Epic" },
  { key: "legendary", label: "Legendary" },
];

const sortOptions = [
  { value: "availability", label: "Sort" },
  { value: "power", label: "Power" },
  { value: "rarity", label: "Rarity" },
  { value: "level", label: "Level" },
  { value: "wins", label: "Wins" },
  { value: "name", label: "Name" },
];

function lower(v) {
  return String(v ?? "").trim().toLowerCase();
}

function normRarity(r) {
  return String(r || "Common").toLowerCase();
}

function truncateCardName(name) {
  const n = String(name || "");
  if (n.length <= MAX_CARD_NAME_LEN) return n;
  return n.slice(0, MAX_CARD_NAME_LEN - 1) + "…";
}

function getCharacterObj(card) {
  const raw = card?.character;

  if (!raw) return null;
  if (typeof raw === "object") return raw;

  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return null;
}

function getCardSide(card) {
  const character = getCharacterObj(card);

  return lower(
    character?.background?.side ||
    character?.body?.side ||
    card?.side ||
    ""
  );
}

function getUserVisualSide(user) {
  const side = lower(user?.side);

  if (side === "bad" || side === "moon") {
    return "moon";
  }

  return "sun";
}

function parseAbilities(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function sanitizeAbilityList(list) {
  return (list || [])
    .map((a) => (typeof a === "string" ? a : a?.name || ""))
    .map((s) => String(s).trim())
    .filter(Boolean);
}

function cardPower(card) {
  const atk = Number(card?.attack ?? 0);
  const def = Number(card?.defense ?? 0);
  const hp = Number(card?.health ?? 0);
  const abilities = sanitizeAbilityList(parseAbilities(card?.abilities));

  return atk + def + hp + abilities.length * 2;
}

function persistSelectedCard(card) {
  if (!card?.id) return;

  localStorage.setItem("selectedCardId", String(card.id));

  localStorage.setItem(
    "selectedCardSnapshot",
    JSON.stringify({
      id: card.id,
      name: card.name,
      rarity: card.rarity,
      attack: card.attack,
      defense: card.defense,
      health: card.health,
      level: card.level,
      wins: card.wins,
      pieces: card.pieces,
      character: card.character,
      side: card.side,
      seasonal_accessory: card.seasonal_accessory ?? null,
      season_name: card.season_name ?? null,
      abilities: card.abilities ?? [],
    })
  );
}

function rarityBorder(rarity) {
  if (rarity === "legendary") return "#f7c247";
  if (rarity === "epic") return "#b266ff";
  if (rarity === "rare") return "#42b7ff";
  return "#d9b27a";
}

function CharacterThumb({ card }) {
  const character = getCharacterObj(card);

  const layers = Object.entries(character || {}).sort(([a], [b]) => {
    const order = ["background", "body", "head"];
    const indexA = order.includes(a) ? order.indexOf(a) : order.length;
    const indexB = order.includes(b) ? order.indexOf(b) : order.length;
    return indexA - indexB;
  });

  return (
    <div style={styles.thumb}>
      {layers.map(([part, data]) => {
        const d = data && typeof data === "object" ? data : {};
        const defaultSrc = `/assets/${part}/${d.side}/${d.rarity}/${d.variant}_thumb.png`;

        const seasonName =
          card?.seasonal_accessory?.season_name || card?.season_name || null;

        const src = getSkinSrc(seasonName, part, defaultSrc);

        const zIndexMap = {
          background: 1,
          body: 2,
          head: 11,
        };

        return (
          <img
            key={part}
            src={src}
            alt={part}
            loading="lazy"
            decoding="async"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: zIndexMap[part] ?? 10,
              pointerEvents: "none",
            }}
          />
        );
      })}
    </div>
  );
}

function BattleProgressBar({ battleLimit }) {
  const used = Math.max(
    0,
    Number(battleLimit?.used ?? 0) || 0
  );

  const limit = Math.max(
    1,
    Number(battleLimit?.limit ?? 10) || 10
  );

  const pct = Math.max(
    0,
    Math.min(100, (used / limit) * 100)
  );

  return (
    <div style={styles.battleProgressWrap}>
      <div style={styles.battleProgressTrack}>
        <div
          style={{
            ...styles.battleProgressFill,
            width: `${pct}%`,
          }}
        />

        <div style={styles.battleProgressText}>
          {used}/{limit}
        </div>
      </div>
    </div>
  );
}

function FighterCard({
  card,
  selected,
  onSelect,
  battleLimit,
  oppositeSide = false,
}) {
  const rarity = normRarity(card?.rarity);
  const abilities = sanitizeAbilityList(
  parseAbilities(card?.abilities)
);

  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.96 }}
      onClick={() => onSelect(card)}
      style={{
  ...styles.cardButton,
  borderColor: selected ? "#000" : "#9c653b",
  boxShadow: "0 3px 0 rgba(64,23,4,0.95)",
  opacity: oppositeSide ? 0.48 : 1,
  filter: oppositeSide ? "grayscale(0.35)" : "none",
}}
    >
      <div style={styles.cardLevel}>{Number(card?.level || 1)}</div>

      <div style={styles.cardArt}>
        <CharacterThumb card={card} />

        {oppositeSide && (
  <div
    style={{
      position: "absolute",
      left: 6,
      bottom: 26,
      zIndex: 50,
      padding: "3px 6px",
      borderRadius: 5,
      background: "rgba(0,0,0,0.78)",
      color: "#ffdf5f",
      fontSize: 10,
      fontWeight: 800,
      textTransform: "uppercase",
      textShadow: "1px 1px #000",
      pointerEvents: "none",
    }}
  >
    Change Side
  </div>
)}

        <div style={styles.abilitiesStack}>
  {abilities.map((ability, idx) => {
    const key = lower(ability);

    return (
      <div
        key={`${ability}_${idx}`}
        style={styles.abilityBubble}
        title={ability}
      >
        <img
          src={`/a_${key}.png`}
          alt={ability}
          style={styles.abilityIcon}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
    );
  })}
</div>

        <div style={styles.cardRarity}>{card?.rarity || "Common"}</div>
      </div>

      <div style={styles.cardName}>
        {truncateCardName(card?.name || "Fighter")}
      </div>

      <div style={styles.cardStats}>
        <span style={styles.stat}>
          <img src="/s_attack.png" alt="" style={styles.statIcon} />
          {Number(card?.attack || 0)}
        </span>

        <span style={styles.stat}>
          <img src="/s_defense.png" alt="" style={styles.statIcon} />
          {Number(card?.defense || 0)}
        </span>

        <span style={styles.stat}>
          <img src="/s_health.png" alt="" style={styles.statIcon} />
          {Number(card?.health || 0)}
        </span>
      </div>

      <BattleProgressBar
  card={card}
  battleLimit={battleLimit}
/>
    </motion.button>
  );
}

function CardPicker({
  user,
  setUser,
  onNavigate,
  onClose,
  onSelected,
  onOpenMerge,
  openedVia = "arena",
  initialRarity = "all",
  persistSelection = true,
  showBuyButton = true,
}) {
  const { shop } = useAppConfig();

  const cardSideChangePrices = useMemo(() => {
  const sparks = Number(
    shop?.cardSidePriceSparks ??
    shop?.CARD_SIDE_PRICE_SPARKS ??
    0
  );

  const runes = Number(
    shop?.cardSidePriceRunes ??
    shop?.CARD_SIDE_PRICE_RUNES ??
    0
  );

  const stars = Number(
    shop?.cardSidePriceStars ??
    shop?.CARD_SIDE_PRICE_STARS ??
    0
  );

  return {
    sparks:
      Number.isFinite(sparks) &&
      sparks > 0
        ? sparks
        : 0,

    runes:
      Number.isFinite(runes) &&
      runes > 0
        ? runes
        : 0,

    stars:
      Number.isInteger(stars) &&
      stars > 0
        ? stars
        : 0,
  };
}, [shop]);

  const [cards, setCards] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [battleLimitsByCardId, setBattleLimitsByCardId] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const [sideChangeCard, setSideChangeCard] = useState(null);
const [sideChangeBusy, setSideChangeBusy] = useState(false);
const [sideChangeError, setSideChangeError] = useState("");

  const [nameQuery, setNameQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [rarityFilter, setRarityFilter] = useState(
  () => {
    const rarity = lower(initialRarity);

    return rarityTabs.some(
      tab => tab.key === rarity
    )
      ? rarity
      : "all";
  }
);
  const [sortKey, setSortKey] = useState("availability");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const selectedId = localStorage.getItem("selectedCardId");

  const rewardPercent = useMemo(() => {
    const raw =
      shop?.rewardPercent ??
      shop?.REWARD_PERCENT ??
      shop?.fight?.rewardPercent ??
      0;

    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [shop]);

  const totalPages = Math.max(1, Math.ceil(Number(totalCards || 0) / PAGE_SIZE));

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(nameQuery.trim());
      setPage(1);
    }, 250);

    return () => window.clearTimeout(id);
  }, [nameQuery]);

  useEffect(() => {
    setPage(1);
  }, [rarityFilter, sortKey, sortDir]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCards() {
      try {
        setIsFetching(true);
        if (cards.length === 0) setLoading(true);

        const token = localStorage.getItem("token");
        const offset = (page - 1) * PAGE_SIZE;

        const params = new URLSearchParams();

params.set("limit", String(PAGE_SIZE));
params.set("offset", String(offset));
params.set("sortKey", sortKey);
params.set("sortDir", sortDir);

/*
 * Fighters Desk ONLY:
 * show SUN + MOON cards.
 */
if (openedVia === "inventoryHub") {
  params.set("includeAllSides", "1");
}

        if (rarityFilter !== "all") {
          params.set("rarity", rarityFilter);
        }

        if (debouncedQuery) {
          params.set("q", debouncedQuery);
        }

        const res = await fetch(
          `${API_BASE}/api/shop/mine?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load cards.");
        }

        if (cancelled) return;

const loadedCards = Array.isArray(data.cards)
  ? data.cards
  : [];

setCards(loadedCards);
setTotalCards(Number(data.total || 0));

const cardIds = loadedCards
  .map((card) => Number(card.id))
  .filter(Number.isFinite);

if (cardIds.length > 0) {
  try {
    const limitsRes = await fetch(
      `${API_BASE}/api/battle/limits`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          cardIds,
        }),
      }
    );

    const limitsData =
      await limitsRes.json().catch(() => ({}));

    if (!cancelled) {
      if (limitsRes.ok) {
        setBattleLimitsByCardId(
          limitsData?.limitsByCardId || {}
        );
      } else {
        console.error(
          "[CardPicker] battle limits failed:",
          limitsData
        );

        setBattleLimitsByCardId({});
      }
    }
  } catch (limitsError) {
    if (!cancelled) {
      console.error(
        "[CardPicker] battle limits failed:",
        limitsError
      );

      setBattleLimitsByCardId({});
    }
  }
} else {
  setBattleLimitsByCardId({});
}

      } catch (err) {
        if (!cancelled) {
          console.error("[CardPicker] failed:", err);
          setCards([]);
          setTotalCards(0);
        }
      } finally {
        if (!cancelled) {
          setIsFetching(false);
          setLoading(false);
        }
      }
    }

    fetchCards();

    return () => {
      cancelled = true;
    };
  }, [
  page,
  rarityFilter,
  debouncedQuery,
  sortKey,
  sortDir,
  openedVia,
]);

  const displayedCards = useMemo(() => {
    const list = Array.isArray(cards) ? [...cards] : [];
    const dir = sortDir === "asc" ? 1 : -1;

    const rarityRank = {
      common: 1,
      rare: 2,
      epic: 3,
      legendary: 4,
    };

    list.sort((a, b) => {
      if (sortKey === "availability") {
  const aLimit =
    battleLimitsByCardId[String(a.id)] ??
    battleLimitsByCardId[Number(a.id)] ??
    {};

  const bLimit =
    battleLimitsByCardId[String(b.id)] ??
    battleLimitsByCardId[Number(b.id)] ??
    {};

  const aUsed = Number(aLimit.used ?? 0);
  const bUsed = Number(bLimit.used ?? 0);

  const aMax = Number(aLimit.limit ?? 10);
  const bMax = Number(bLimit.limit ?? 10);

  const aLocked =
    aUsed >= aMax ? 1 : 0;

  const bLocked =
    bUsed >= bMax ? 1 : 0;

  if (aLocked !== bLocked) {
    return aLocked - bLocked;
  }

  return cardPower(b) - cardPower(a);
}

      if (sortKey === "level") {
        return (Number(a.level || 0) - Number(b.level || 0)) * dir;
      }

      if (sortKey === "wins") {
        return (Number(a.wins || 0) - Number(b.wins || 0)) * dir;
      }

      if (sortKey === "rarity") {
        return (
          ((rarityRank[normRarity(a.rarity)] || 0) -
            (rarityRank[normRarity(b.rarity)] || 0)) *
          dir
        );
      }

      if (sortKey === "name") {
        return String(a.name || "").localeCompare(String(b.name || "")) * dir;
      }

      return (cardPower(a) - cardPower(b)) * dir;
    });

    return list;
  }, [
  cards,
  sortKey,
  sortDir,
  battleLimitsByCardId,
]);

  const continueSelectCard = (card) => {
  if (persistSelection) {
    persistSelectedCard(card);
  }

  if (typeof onSelected === "function") {
    onSelected({
      cardId: card.id,
      card,
    });

    return;
  }

  onNavigate?.("arenas", {
    cardId: card.id,
    openedVia,
  });
};

const handleSelect = (card) => {
  if (!card) return;

  /*
   * IMPORTANT:
   *
   * Side validation happens ONLY inside Fighters Desk.
   *
   * Arena / Defense Deck / Item Claim / other CardPickers
   * keep exactly the old behavior.
   */
  if (openedVia === "inventoryHub") {
    const cardSide = getCardSide(card);
    const userSide = getUserVisualSide(user);

    if (
      cardSide &&
      userSide &&
      cardSide !== userSide
    ) {
      setSideChangeError("");
      setSideChangeCard(card);
      return;
    }
  }

  continueSelectCard(card);
};


const changeCardSide = async (payWith) => {
  if (!sideChangeCard?.id) return;
  if (sideChangeBusy) return;

  const currency = lower(payWith);

  if (!["sparks", "runes"].includes(currency)) {
    return;
  }

  const price =
    Number(cardSideChangePrices[currency] || 0);

  if (price <= 0) {
    setSideChangeError(
      "This payment method is currently unavailable."
    );
    return;
  }

  const balance =
    Number(user?.[currency] || 0);

  if (balance < price) {
    setSideChangeError(
      currency === "sparks"
        ? "You do not have enough Sparks."
        : "You do not have enough Runes."
    );

    return;
  }

  try {
    setSideChangeBusy(true);
    setSideChangeError("");

    const token =
      localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/api/cards/${encodeURIComponent(
        sideChangeCard.id
      )}/change-side`,
      {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify({
          payWith: currency,
        }),
      }
    );

    const data =
      await res.json().catch(() => ({}));

    if (!res.ok) {
      const errors = {
        INSUFFICIENT_SPARKS:
          "You do not have enough Sparks.",

        INSUFFICIENT_RUNES:
          "You do not have enough Runes.",

        CARD_FOR_SALE:
          "Remove this card from the marketplace first.",

        CARD_HAS_PENDING_BATTLE:
          "This card has a pending battle.",

        PAYMENT_METHOD_DISABLED:
          "This payment method is currently unavailable.",

        INVALID_CARD_SIDE:
          "This card does not have a valid side.",
      };

      throw new Error(
        errors[data?.error] ||
        data?.message ||
        data?.error ||
        "Failed to change card side."
      );
    }

    if (!data.card) {
      throw new Error(
        "Server did not return the updated card."
      );
    }

  const changedCard = data.card;

  

/*
 * Update card inside Fighters Desk.
 */
setCards((prev) =>
  prev.map((card) =>
    String(card.id) === String(changedCard.id)
      ? {
          ...card,
          ...changedCard,
        }
      : card
  )
);

/*
 * Update global user balance immediately.
 */
if (
  data.user &&
  typeof setUser === "function"
) {
  setUser((prev) => ({
    ...prev,
    sparks:
      data.user.sparks !== undefined
        ? Number(data.user.sparks)
        : prev?.sparks,

    runes:
      data.user.runes !== undefined
        ? Number(data.user.runes)
        : prev?.runes,
  }));
}

/*
 * Close Change Side modal only.
 */
setSideChangeCard(null);
setSideChangeError("");

  } catch (err) {
    console.error(
      "[CardPicker] side change failed:",
      err
    );

    setSideChangeError(
      err?.message ||
      "Failed to change card side."
    );

  } finally {
    setSideChangeBusy(false);
  }
};

const wait = (ms) =>
  new Promise((resolve) =>
    setTimeout(resolve, ms)
  );

const pollStarsOrder = async (orderId) => {
  const token = localStorage.getItem("token");

  for (let tries = 0; tries < 20; tries += 1) {
    try {
      const res = await fetch(
        `${API_BASE}/api/telegram/stars/order/${encodeURIComponent(
          orderId
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.ok) {
        const data = await res.json().catch(() => ({}));

        if (data?.status === "paid") {
          return true;
        }

        if (
          data?.status === "failed" ||
          data?.status === "cancelled"
        ) {
          return false;
        }
      }
    } catch {}

    await wait(1500);
  }

  return false;
};

const changeCardSideWithStars = async () => {
  if (!sideChangeCard?.id) return;
  if (sideChangeBusy) return;

  const starsPrice = Number(
    cardSideChangePrices.stars || 0
  );

  if (
    !Number.isInteger(starsPrice) ||
    starsPrice <= 0
  ) {
    setSideChangeError(
      "Telegram Stars payment is currently unavailable."
    );
    return;
  }

  const telegram = window.Telegram?.WebApp;

  if (!telegram?.openInvoice) {
    setSideChangeError(
      "Telegram Stars payments are only available inside Telegram."
    );
    return;
  }

  try {
    setSideChangeBusy(true);
    setSideChangeError("");

    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/api/cards/${encodeURIComponent(
        sideChangeCard.id
      )}/change-side/stars/create-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.ok) {
      const errors = {
        CARD_FOR_SALE:
          "Remove this card from the marketplace first.",

        CARD_HAS_PENDING_BATTLE:
          "This card has a pending battle.",

        PAYMENT_METHOD_DISABLED:
          "Telegram Stars payment is currently unavailable.",

        CARD_NOT_IN_ACTIVE_SEASON:
          "This card is not from the active season.",

        INVALID_CARD_SIDE:
          "This card does not have a valid side.",
      };

      throw new Error(
        errors[data?.error] ||
        data?.error ||
        "Could not start Telegram Stars payment."
      );
    }

    const { orderId, invoiceLink } = data;

    if (!orderId || !invoiceLink) {
      throw new Error(
        "Invalid Telegram Stars payment response."
      );
    }

    telegram.openInvoice(
      invoiceLink,
      async (status) => {
        if (status !== "paid") {
          setSideChangeBusy(false);

          if (status === "cancelled") {
            return;
          }

          setSideChangeError(
            "Telegram Stars payment failed."
          );

          return;
        }

        try {
          const confirmed =
            await pollStarsOrder(orderId);

          if (!confirmed) {
            throw new Error(
              "Payment was received, but the card change is still being confirmed."
            );
          }

          const cardRes = await fetch(
            `${API_BASE}/api/shop/cards/${encodeURIComponent(
              sideChangeCard.id
            )}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const cardData =
            await cardRes.json().catch(() => ({}));

          if (
            !cardRes.ok ||
            !cardData?.card
          ) {
            throw new Error(
              "Card side changed, but the card could not be refreshed."
            );
          }

          const changedCard =
            cardData.card;

          setCards((prev) =>
            prev.map((card) =>
              String(card.id) ===
              String(changedCard.id)
                ? {
                    ...card,
                    ...changedCard,
                  }
                : card
            )
          );

          setSideChangeCard(null);
          setSideChangeError("");

        } catch (err) {
          console.error(
            "[CardPicker] Stars side change:",
            err
          );

          setSideChangeError(
            err?.message ||
            "Failed to confirm Telegram Stars payment."
          );

        } finally {
          setSideChangeBusy(false);
        }
      }
    );

  } catch (err) {
    console.error(
      "[CardPicker] Stars side change:",
      err
    );

    setSideChangeError(
      err?.message ||
      "Failed to start Telegram Stars payment."
    );

    setSideChangeBusy(false);
  }
};

  const goBuyCard = () => {
    onClose?.();
    onNavigate?.("shop", { initialView: "cards" });
  };

  const canOpenMerge =
  openedVia === "inventory" || openedVia === "inventoryHub";

const openMergeCards = () => {
  if (!canOpenMerge) return;

  if (typeof onOpenMerge === "function") {
    onOpenMerge();
    return;
  }

  onClose?.();
  onNavigate?.("cardMerge");
};

  return (

    <div className="modal_body">
    

      <div className="modal_inside_2" style={{padding: 25}}>
  
        <div className="info" style={{margin: -10, marginTop: -10, marginBottom: 10, fontFamily: '"Fredoka", sans-serif',width: "calc(100% + 20px)"}}>

        {![
  "inventoryHub",
  "inventory",
  "defenseDeck",
  "defense",
  "itemClaim",
].includes(openedVia) && (
          <div style={styles.payout} className="shadow1">
            Current Payout:{" "}
            <span style={styles.payoutValue}>
              {(100 + rewardPercent) / 100}x
            </span>
          </div>
        )}

        <div style={styles.searchRow}>
          <input
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Search by name..."
            style={styles.searchInput}
          />

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={styles.sortSelect}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            style={styles.sortDirButton}
            title="Toggle sort direction"
          >
            <img
              src="/v2/down.png"
              alt=""
              style={{
                width: "100%",
                height: "100%",
                transform:
                  sortDir === "asc" ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>
        </div>

        {canOpenMerge && (
  <button
    type="button"
    onClick={openMergeCards}
    className="game-button yellow-button"
    style={{
      width: "100%",
      marginTop: 10,
      marginBottom: 5,
      fontFamily: '"Fredoka", sans-serif',
    }}
  >
    Merge Cards
  </button>
)}

        <div style={styles.tabs}>
          {rarityTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRarityFilter(tab.key)}
              style={{
                ...styles.tabButton,
                background:
                  rarityFilter === tab.key
                    ? "#e3be47"
                    : "#492614",
                color: rarityFilter === tab.key ? "#000" : "#fff",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        </div>


        <div style={{float: "left", width: "calc(100% + 20px)",marginLeft: -10, marginRight: -10}}>
          <div style={styles.gridWrap}>
            {loading ? (
  <div
  style={{
    gridColumn: "1 / -1",
    width: "100%",
    minHeight: 690,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateY(-115px)",
  }}
>
  <LoadingSpinner text="Loading Fighters..." size={56} />
</div>
) : (
  displayedCards.map((card) => {
    const cardSide = getCardSide(card);
    const userSide = getUserVisualSide(user);

    const oppositeSide =
      openedVia === "inventoryHub" &&
      cardSide &&
      userSide &&
      cardSide !== userSide;

    return (
      <FighterCard
        key={card.id}
        card={card}
        selected={String(selectedId || "") === String(card.id)}
        onSelect={handleSelect}
        oppositeSide={oppositeSide}
        battleLimit={
          battleLimitsByCardId[String(card.id)] ??
          battleLimitsByCardId[Number(card.id)] ??
          null
        }
      />
    );
  })
)}
</div>

          {isFetching && !loading && <div style={styles.fetching}>Updating...</div>}

          

        </div>

<div className="info" style={{margin: -10, marginTop: 15, marginBottom: -10, fontFamily: '"Fredoka", sans-serif',width: "calc(100% + 20px)"}}>

  <div style={styles.paginationLeft}>
    <button
      type="button"
      disabled={page <= 1}
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      style={{
        ...styles.navPageButton,
        opacity: page <= 1 ? 0.45 : 1,
        cursor: page <= 1 ? "not-allowed" : "pointer",
      }}
    >
      <img src="/v2/left.png" alt="Previous" style={styles.navArrowIcon} />
    </button>

    <div style={styles.pageLabel}>
      Page {page} <span style={styles.pageTotal}>/ {totalPages}</span>
    </div>

    <button
      type="button"
      disabled={page >= totalPages}
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      style={{
        ...styles.navPageButton,
        opacity: page >= totalPages ? 0.45 : 1,
        cursor: page >= totalPages ? "not-allowed" : "pointer",
      }}
    >
      <img src="/v2/right.png" alt="Next" style={styles.navArrowIcon} />
    </button>
  </div></div>

        </div>



       <div className="modal_footer">
  <button
    type="button"
    onClick={goBuyCard}
    className="game-button green-button"
  >
    Buy New Cards
  </button>
</div>

{sideChangeCard && (() => {
  const currentSide = getCardSide(sideChangeCard);
  const targetSide = getUserVisualSide(user);

  const sparksPrice =
  Number(
    cardSideChangePrices.sparks ||
    0
  );

const runesPrice =
  Number(
    cardSideChangePrices.runes ||
    0
  );

const starsPrice =
  Number(
    cardSideChangePrices.stars ||
    0
  );

  return (
    <div
      className="modal_box"
      
    >
      <div className="modal_title">
        Change Card Side

        <button
          type="button"
          className="modal_close"
          disabled={sideChangeBusy}
          onClick={() => {
            setSideChangeCard(null);
            setSideChangeError("");
          }}
        />
      </div>

      <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
        <div className="modal_inside">

          <div
            className="shadow2"
            style={{
              fontSize: 28,
              fontWeight: 600,
              marginBottom: 15,
            }}
          >
            Wrong Side
          </div>

          <div
            style={{
              position: "relative",
              width: 150,
              height: 150,
              margin: "0 auto 15px auto",
              overflow: "hidden",
              borderRadius: 8,
            }}
          >
            <CharacterThumb card={sideChangeCard} />
          </div>

          <div
            className="shadow1"
            style={{
              fontSize: 15,
              fontWeight: 600,
              marginBottom: 5,
            }}
          >
            This fighter belongs to the opposite side.
          </div>

         <div
  className="info"
  style={{
    fontSize: 18,
    fontWeight: 600,
  }}
>
  {sparksPrice > 0 && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 5,
      }}
    >
      <span>COST:</span>

      <img
        src="/sparks.png"
        alt="Sparks"
        style={{
          width: 22,
          height: 22,
          objectFit: "contain",
        }}
      />

      <span>
        {sparksPrice.toLocaleString()} SPARKS
      </span>
    </div>
  )}

  {runesPrice > 0 && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 5,
      }}
    >
      <span>COST:</span>

      <img
        src="/runes.png"
        alt="Runes"
        style={{
          width: 22,
          height: 22,
          objectFit: "contain",
        }}
      />

      <span>
        {runesPrice.toLocaleString()} RUNES
      </span>
    </div>
  )}

  {starsPrice > 0 && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 5,
      }}
    >
      <span>COST:</span>

      <img
        src="/star.png"
        alt="Stars"
        style={{
          width: 22,
          height: 22,
          objectFit: "contain",
        }}
      />

      <span>
        {starsPrice.toLocaleString()} STARS
      </span>
    </div>
  )}
</div>

          {sideChangeError && (
            <div className="info"
              style={{
                color: "#ff7979",
                fontSize: 15,
                fontWeight: 600,
                marginTop: 10,
              }}
            >
              {sideChangeError}
            </div>
          )}

        </div>

       <div className="modal_footer">
  {sparksPrice > 0 && (
    <button
      type="button"
      className="game-button green-button"
      style={{ marginRight: 10 }}
      disabled={sideChangeBusy}
      onClick={() => changeCardSide("sparks")}
    >
      {sideChangeBusy
        ? "CHANGING..."
        : "CHANGE"}
    </button>
  )}

  {runesPrice > 0 && (
    <button
      type="button"
      className="game-button green-button"
      style={{ marginRight: 10 }}
      disabled={sideChangeBusy}
      onClick={() => changeCardSide("runes")}
    >
      {sideChangeBusy
        ? "CHANGING..."
        : "CHANGE"}
    </button>
  )}

  {starsPrice > 0 && (
    <button
      type="button"
      className="game-button green-button"
      style={{ marginRight: 10 }}
      disabled={sideChangeBusy}
      onClick={changeCardSideWithStars}
    >
      {sideChangeBusy
        ? "CHANGING..."
        : "CHANGE"}
    </button>
  )}

  <button
    type="button"
    className="game-button red-button"
    disabled={sideChangeBusy}
    onClick={() => {
      setSideChangeCard(null);
      setSideChangeError("");
    }}
  >
    CANCEL
  </button>
</div>
      </motion.div>
    </div>
  );
})()}

</div>
);
}

const styles = {
  wrap: {
    width: "100%",
    minHeight: "100%",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },

  panel: {
    position: "relative",
    width: "100%",
    maxWidth: 330,
    margin: "0 auto",
    padding: 8,
    paddingBottom: 10,
    borderRadius: 8,
    background: "linear-gradient(180deg, #7c3f16 0%, #4b210b 100%)",
    border: "3px solid #f1b45d",
    boxShadow:
      "0 4px 0 #2a1004, inset 0 0 0 2px rgba(64,23,4,0.85), 0 8px 18px rgba(0,0,0,0.45)",
    boxSizing: "border-box",
    fontFamily: '"Fredoka", sans-serif',
  },

  payout: {
    color: "#fff",
    textAlign: "center",
    fontSize: 14,
    lineHeight: "16px",
    fontWeight: 900,
    textTransform: "uppercase",
    marginBottom: 10,
  },

  payoutValue: {
    color: "#fff04e",
  },

  searchRow: {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 58px 28px",
  gap: 4,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  marginBottom: 6,
},

searchInput: {
  width: "100%",
  minWidth: 0,

  height: 28,
  borderRadius: 4,
  border: "2px solid #2d1205",
  background: "#fff",
  color: "#2b1206",
  fontSize: 12,
  padding: "0 7px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Arial, sans-serif",
  fontWeight: 700,
},
sortSelect: {
  width: "100%",
  minWidth: 0,

  height: 28,
  borderRadius: 4,
  border: "2px solid #2d1205",
  background: "#fff",
  color: "#2b1206",
  fontSize: 11,
  padding: "0 3px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Arial, sans-serif",
  fontWeight: 700,
},

  sortDirButton: {
    height: 28,
    borderRadius: 4,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    padding: 0,
    border: 0
  },

tabs: {
  display: "flex",
  alignItems: "center",
  gap: 2,
  width: "100%",
  marginBottom: 0,
  marginTop: 10
},

tabButton: {
  height: 28,
  border: "2px solid #7f5031",
  borderRadius: 6,
  fontSize: 11,
  lineHeight: "22px",
  fontWeight: 600,
  cursor: "pointer",
  padding: "0 5px",
  fontFamily: '"Fredoka", sans-serif',
  whiteSpace: "nowrap",
  flex: "1 1 auto",
  width: "auto",
  minWidth: "max-content",
},

gridWrap: {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 7,
  alignItems: "start",
},

  cardButton: {
    position: "relative",
    alignSelf: "start",
    minWidth: 0,
    borderRadius: 6,
    border: "2px solid #d9b27a",
    background: "linear-gradient(180deg, #f3b36a 0%, #7a3813 100%)",
    padding: 4,
    cursor: "pointer",
    overflow: "hidden",
    fontFamily: '"Fredoka", sans-serif',
  },

cardLevel: {
  position: "absolute",
  top: 10,
  left: 8,
  width: 28,
  height: 28,
  backgroundImage: "url('/v2/position.png')",
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  color: "#fff",
  fontSize: 15,
  lineHeight: "28px",
  textAlign: "center",
  zIndex: 20,
  fontFamily: '"Fredoka", sans-serif',
  fontWeight: 600,
},

  cardWins: {
    position: "absolute",
    top: 1,
    right: 2,
    height: 17,
    minWidth: 22,
    paddingLeft: 13,
    paddingRight: 2,
    borderRadius: 9,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 11,
    lineHeight: "17px",
    zIndex: 20,
    textShadow: "1px 1px #000",
  },

  cardWinsIcon: {
    position: "absolute",
    left: 1,
    top: 2,
    width: 13,
    height: 13,
    objectFit: "contain",
  },

  cardArt: {
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",
  marginBottom: 3,
  borderRadius: 4,
  background:
    "radial-gradient(circle at 50% 30%, rgba(255,255,255,0.35), rgba(91,42,11,0.15) 42%, rgba(36,15,4,0.45) 100%)",
  overflow: "hidden",
},

  thumb: {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
},

abilitiesStack: {
  position: "absolute",
  top: 5,
  right: 5,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  zIndex: 35,
},

abilityBubble: {
  width: 24,
  height: 24,
  display: "grid",
  placeItems: "center",
},

abilityIcon: {
  width: 21,
  height: 21,
  objectFit: "contain",
},

  cardRarity: {
    zIndex: 20,
    textTransform: "uppercase",
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    color: "#fff",
    height: 21,
    fontSize: 11,
    lineHeight: "21px",
    textAlign: "center",
    fontWeight: 600,
    background: "rgba(77, 44, 36, 0.65)",
  },

  cardName: {
    display: "none",
    height: 15,
    color: "#fff",
    fontSize: 12,
    lineHeight: "15px",
    textAlign: "center",
    textShadow: "1px 1px #000",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  cardStats: {
    height: 24,
    borderRadius: 4,
    background: "rgba(0,0,0,0.58)",
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    alignItems: "center",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "24px",
  },

  stat: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
  },

  statIcon: {
    width: 12,
    height: 12,
    objectFit: "contain",
  },

  battleProgressWrap: {
    width: "100%",
    marginTop: 3,
    boxSizing: "border-box",
  },

  battleProgressTrack: {
    height: 21,
    width: "100%",
    borderRadius: 4,
    background: "#1A0101",
    border: "1px solid rgba(255,255,255,0.22)",
    overflow: "hidden",
    position: "relative",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.55)",
    boxSizing: "border-box",
  },

  battleProgressFill: {
    height: "100%",
    background: "linear-gradient(180deg, #7CF5C4 0%, #6EE5D0 38%, #5F83F1 100%)",
    borderRadius: 4,
  },

  battleProgressText: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 12,
    fontWeight: 900,
    fontFamily: '"Fredoka", sans-serif',
    textShadow: "1px 1px 2px #000",
    lineHeight: "18px",
  },

  message: {
    gridColumn: "1 / 3",
    color: "#fff",
    textAlign: "center",
    fontSize: 20,
    lineHeight: "22px",
    paddingTop: 60,
    fontWeight: 600,
  },

  fetching: {
    color: "#fff04e",
    fontSize: 12,
    textAlign: "center",
    marginTop: 4,
    textShadow: "1px 1px #000",
  },

  footer: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  width: "100%",
  boxSizing: "border-box",
  marginTop: 7,
},

paginationLeft: {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  minWidth: 0,
  flex: 1,
},

navPageButton: {
  width: 42,
  height: 42,
  border: 0,
  padding: 0,
  background: "transparent"
},

navArrowIcon: {
  width: 44,
  height: 44,
  objectFit: "contain",
  display: "block",
  pointerEvents: "none",
},

pageLabel: {
  height: 42,
  lineHeight: "38px",
  borderRadius: 8,
  background: "rgba(0,0,0,0.45)",
  border: "2px solid #7f5031",
  color: "#fff",
  fontSize: 14,
  textTransform: "uppercase",
  fontWeight: 600,
  textAlign: "center",
  textShadow: "1px 1px #000",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  fontFamily: '"Fredoka", sans-serif',
  padding: "0px 15px",
},

pageTotal: {
  color: "#ffdf5f",
},

buyButton: {
  height: 34,
  minWidth: 92,
  padding: "0 12px",
  borderRadius: 8,
  border: "2px solid #145c2f",
  borderBottom: "4px solid #145c2f",
  background: "linear-gradient(#67df70, #239a3f)",
  color: "#fff",
  fontSize: 14,
  lineHeight: "26px",
  fontWeight: 800,
  textShadow: "1px 1px #000",
  cursor: "pointer",
  fontFamily: '"Fredoka", sans-serif',
  whiteSpace: "nowrap",
},


};

export default CardPicker;